import { useState, useEffect } from 'react';
import Icon from '../atoms/Icon';
import SessionManager from '../../utils/sessionManager';

const adminAuthHeaders = (extra = {}) => {
  const session = SessionManager.getSession('super_admin') || SessionManager.getCurrentSession();
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const DemoLeadsManager = ({ adminId }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total_leads: 0,
    new_leads: 0,
    contacted_leads: 0,
    converted_leads: 0,
    closed_leads: 0,
    conversion_rate: 0
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', notes: '' });

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [statusFilter]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const url = new URL('https://aptirix-version-610696728606.asia-south1.run.app/api/demo-leads/');
      if (statusFilter) {
        url.searchParams.append('status_filter', statusFilter);
      }

      const response = await fetch(url, { headers: adminAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const url = new URL('https://aptirix-version-610696728606.asia-south1.run.app/api/demo-leads/stats');

      const response = await fetch(url, { headers: adminAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleUpdateLead = async () => {
    try {
      const url = new URL(`https://aptirix-version-610696728606.asia-south1.run.app/api/demo-leads/${selectedLead.id}`);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: { ...adminAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updateForm)
      });

      if (response.ok) {
        setShowUpdateModal(false);
        setSelectedLead(null);
        setUpdateForm({ status: '', notes: '' });
        loadLeads();
        loadStats();
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      const url = new URL(`https://aptirix-version-610696728606.asia-south1.run.app/api/demo-leads/${leadId}`);

      const response = await fetch(url, { method: 'DELETE', headers: adminAuthHeaders() });
      if (response.ok) {
        loadLeads();
        loadStats();
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const openUpdateModal = (lead) => {
    setSelectedLead(lead);
    setUpdateForm({ status: lead.status, notes: lead.notes || '' });
    setShowUpdateModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      converted: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      new: 'fiber_new',
      contacted: 'phone',
      converted: 'check_circle',
      closed: 'cancel'
    };
    return icons[status] || 'info';
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
            </div>
            <Icon name="people" size={32} className="text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New</p>
              <p className="text-2xl font-bold text-gray-900">{stats.new_leads}</p>
            </div>
            <Icon name="fiber_new" size={32} className="text-indigo-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Contacted</p>
              <p className="text-2xl font-bold text-gray-900">{stats.contacted_leads}</p>
            </div>
            <Icon name="phone" size={32} className="text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Converted</p>
              <p className="text-2xl font-bold text-gray-900">{stats.converted_leads}</p>
            </div>
            <Icon name="check_circle" size={32} className="text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Closed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.closed_leads}</p>
            </div>
            <Icon name="cancel" size={32} className="text-gray-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Conversion</p>
              <p className="text-2xl font-bold text-gray-900">{stats.conversion_rate.toFixed(1)}%</p>
            </div>
            <Icon name="trending_up" size={32} className="text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-semibold text-gray-700">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Leads</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="closed">Closed</option>
          </select>
          <button
            onClick={loadLeads}
            className="ml-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Icon name="refresh" size={20} />
            Refresh
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Recruiter</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      Loading leads...
                    </div>
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No leads found
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{lead.company_name}</div>
                      {lead.context && (
                        <div className="text-sm text-gray-500 mt-1">{lead.context}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-900">{lead.recruiter_name}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <a href={`mailto:${lead.email}`} className="text-indigo-600 hover:underline flex items-center gap-1">
                          <Icon name="mail" size={16} />
                          {lead.email}
                        </a>
                        <a href={`tel:${lead.phone_number}`} className="text-gray-600 hover:underline flex items-center gap-1 mt-1">
                          <Icon name="phone" size={16} />
                          {lead.phone_number}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status)}`}>
                        <Icon name={getStatusIcon(lead.status)} size={16} />
                        {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openUpdateModal(lead)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Update Lead"
                        >
                          <Icon name="edit" size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Lead"
                        >
                          <Icon name="delete" size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-indigo-600 text-white p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-lg font-bold">Update Lead</h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
              >
                <Icon name="close" size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600">Company: <span className="font-semibold text-gray-900">{selectedLead.company_name}</span></p>
                <p className="text-sm text-gray-600">Recruiter: <span className="font-semibold text-gray-900">{selectedLead.recruiter_name}</span></p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={updateForm.status}
                  onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="converted">Converted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea
                  value={updateForm.notes}
                  onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                  placeholder="Add notes about this lead..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLead}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Update Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoLeadsManager;
